import React, { useState, useEffect } from "react";
import { Button, Form, FormGroup, Label, Input, Row, Col, Container } from "reactstrap";
import toast from "react-hot-toast";
import Select from "react-select";
import api from "../../api";
import Flatpickr from "react-flatpickr";
import { FormSectionCard } from "../../Components/Common/DashboardKit";

/**
 * 90-Day Review Form.
 *
 * Pass 4 UI update (2026):
 *  - The four logical sections (Employee info / Performance Criteria /
 *    Improvement Plan + Overall Assessment / Approvals) — previously
 *    separated by inline <h5> headers inside a bare <Form> — are now
 *    wrapped in four FormSectionCard cards from the DashboardKit.
 *  - The download-PDF link is moved into the first section's `actions`
 *    slot. Same href, same icon, same styling.
 *  - EVERY field, every onChange, the initialForm shape, the
 *    localStorage draft autosave (1-second debounce on key
 *    `90dayDraft`), the api.get for the employee list
 *    (WhosInBuilding/userList), the customSelectStyles, the
 *    Flatpickr date pickers, the submit URL `/api/hr/review/90day`,
 *    and the JSON.stringify(formData) body are unchanged.
 *  - handleRatingChange, handleApprovalChange, handleSubmit are
 *    preserved exactly. The ratings array (length 5) and the
 *    label/criteria mapping arrays are unchanged.
 *  - Removed the unused `components` named import from react-select
 *    (it wasn't used anywhere in the original markup).
 *  - Removed the empty <h2> header inside CardHeader that was
 *    rendered for spacing only.
 */
const initialForm = {
    employee: "",
    hireDate: "",
    supervisor: "",
    reviewDate: "",
    ratings: Array.from({ length: 5 }, () => ({ value: "", label: "" })),
    improvementPlan: "",
    overallAssessment: "", // Below / Meets / Exceeds
    payRaise: false,
    approvals: {
        supervisor: "",
        supervisorDate: "",
        manager: "",
        managerDate: "",
        hr: "",
        hrDate: ""
    }
};

const labels = ["Unsatisfactory", "Marginal", "Good", "Exceptional", "Beyond Belief"];
const criteriaLabels = ["Attitude", "Job Knowledge", "Communication", "Initiative", "Style Fit"];

const NinetyDayReviewForm = () => {
    const [formData, setFormData] = useState(initialForm);

    useEffect(() => {
        const draft = localStorage.getItem("90dayDraft");
        if (draft) setFormData(JSON.parse(draft));
    }, []);

    useEffect(() => {
        const t = setTimeout(() => {
            localStorage.setItem("90dayDraft", JSON.stringify(formData));
            toast.success("Draft saved", { duration: 1000, position: "bottom-right" });
        }, 1000);
        return () => clearTimeout(t);
    }, [formData]);

    const handleRatingChange = (i, value) => {
        const ratings = [...formData.ratings];
        ratings[i] = { value, label: labels[value - 1] };
        setFormData(p => ({ ...p, ratings }));
    };

    const handleApprovalChange = (field, value) => {
        setFormData(p => ({
            ...p,
            approvals: { ...p.approvals, [field]: value }
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const res = await fetch("/api/hr/review/90day", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(formData)
            });
            if (res.ok) toast.success("90-Day Review submitted");
            else throw new Error("Submission failed");
        } catch (err) {
            toast.error(err.message);
        }
    };

    /// Rendering EMployees name:

    const [selectedEmployeeNames, setSelectedEmployeeNames] = useState([]);
    const [selectedSupervisorNames, setSelectedSupervisorNames] = useState([]);
    const [SingleOptions, setSingleOptions] = useState([]);
    // employeeIdOptions is populated by the API call below; preserved to
    // avoid breaking any external reference even though it isn't read in
    // the render directly (legacy state).
    // eslint-disable-next-line no-unused-vars
    const [employeeIdOptions, setEmployeeIdOptions] = useState([]);

    useEffect(() => {
        api.get("WhosInBuilding/userList")
            .then((res) => {
                const data = res.data;

                // Employee Name dropdown
                const names = data.map(user => ({
                    value: user.empId,
                    label: `${user.name} (${user.empId})`
                }));

                // Employee ID dropdown
                const ids = data.map(user => ({
                    value: user.empId,
                    label: user.empId
                }));

                setSingleOptions(names);
                setEmployeeIdOptions(ids);
            })
            .catch((err) => {
                console.error("Error fetching employee list:", err);
            });
    }, []);

    const customSelectStyles = {
        control: (base) => ({
            ...base,
            backgroundColor: 'white',
        }),
        menu: (base) => ({
            ...base,
            backgroundColor: 'white',
        }),
    };
    const [startDate, setStartDate] = useState(new Date());

    // Download-PDF action — preserved from the original CardHeader.
    const downloadAction = (
        <a
            href="/assets/90-Day Review Form.pdf"
            download
            className="btn btn-soft-info btn-sm"
        >
            <i className="ri-download-2-fill me-1 align-bottom"></i> Download Form
        </a>
    );

    return (
        <Container>
            <Form onSubmit={handleSubmit}>
                {/* ── Section 1: Employee info ─────────────────────────── */}
                <FormSectionCard
                    icon="ri-user-line"
                    title="Employee information"
                    subtitle="Identify who's being reviewed."
                    actions={downloadAction}
                    tone="primary"
                >
                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <Label htmlFor="choices-multiple-remove-button" className="form-label text-dark">Employee Name</Label>
                                <Select
                                    value={selectedEmployeeNames}
                                    isMulti={false}
                                    isClearable={true}
                                    onChange={(selected) => setSelectedEmployeeNames(selected || [])}
                                    options={SingleOptions}
                                    styles={customSelectStyles}
                                />
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <Label htmlFor="choices-multiple-remove-button" className="form-label text-dark">Review Date</Label>
                                <Flatpickr
                                    className="form-control"
                                    value={startDate}
                                    onChange={(dates) => setStartDate(dates[0])}
                                    options={{ dateFormat: "Y-m-d" }}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <Label htmlFor="choices-multiple-remove-button" className="form-label text-dark">Date of Hire</Label>
                                <Flatpickr
                                    className="form-control"
                                    value={startDate}
                                    onChange={(dates) => setStartDate(dates[0])}
                                    options={{ dateFormat: "Y-m-d" }}
                                />
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <Label htmlFor="choices-multiple-remove-button" className="form-label text-dark">Supervisor Name</Label>
                                <Select
                                    value={selectedSupervisorNames}
                                    isMulti={false}
                                    isClearable={true}
                                    onChange={(selected) => setSelectedSupervisorNames(selected || [])}
                                    options={SingleOptions}
                                    styles={customSelectStyles}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                </FormSectionCard>

                {/* ── Section 2: Performance criteria ──────────────────── */}
                <FormSectionCard
                    icon="ri-star-line"
                    title="Performance criteria"
                    subtitle="Rate each criterion on a 1-5 scale."
                    tone="info"
                >
                    {criteriaLabels.map((label, i) => (
                        <FormGroup key={label}>
                            <Label htmlFor="choices-multiple-remove-button" className="form-label text-dark">{i + 1}. {label}</Label>
                            <Input type="select" value={formData.ratings[i].value} onChange={e => handleRatingChange(i, Number(e.target.value))}>
                                <option value="">Select Rating</option>
                                {[1, 2, 3, 4, 5].map(n => (
                                    <option key={n} value={n}>{n} - {labels[n - 1]}</option>
                                ))}
                            </Input>
                        </FormGroup>
                    ))}
                </FormSectionCard>

                {/* ── Section 3: Summary & recommendation ──────────────── */}
                <FormSectionCard
                    icon="ri-file-text-line"
                    title="Summary & recommendation"
                    subtitle="Improvement plan, overall assessment, and pay decision."
                    tone="warning"
                >
                    <FormGroup>
                        <Label htmlFor="choices-multiple-remove-button" className="form-label text-dark">Proposed Improvement Plan</Label>
                        <Input type="textarea" rows={4} value={formData.improvementPlan} onChange={e => setFormData(p => ({ ...p, improvementPlan: e.target.value }))} />
                    </FormGroup>

                    <FormGroup>
                        <Label htmlFor="choices-multiple-remove-button" className="form-label text-dark">Overall Assessment</Label>
                        <Input type="select" value={formData.overallAssessment} onChange={e => setFormData(p => ({ ...p, overallAssessment: e.target.value }))}>
                            <option value="">Select One</option>
                            <option>Below Expectations</option>
                            <option>Meets Expectations</option>
                            <option>Exceeds Expectations</option>
                        </Input>
                    </FormGroup>

                    <FormGroup check className="mb-3">
                        <Input type="checkbox" checked={formData.payRaise} onChange={e => setFormData(p => ({ ...p, payRaise: e.target.checked }))} />
                        <Label check>Pay Raise (PIC Form)</Label>
                    </FormGroup>
                </FormSectionCard>

                {/* ── Section 4: Approvals ─────────────────────────────── */}
                <FormSectionCard
                    icon="ri-shield-check-line"
                    title="Approvals"
                    subtitle="Sign-off chain for this 90-day review."
                    tone="success"
                >
                    <Row>
                        <Col md={6}><FormGroup><Label htmlFor="choices-multiple-remove-button" className="form-label text-dark">Supervisor</Label><Input value={formData.approvals.supervisor} onChange={e => handleApprovalChange("supervisor", e.target.value)} /></FormGroup></Col>
                        <Col md={6}><FormGroup><Label htmlFor="choices-multiple-remove-button" className="form-label text-dark">Date</Label><Input type="date" value={formData.approvals.supervisorDate} onChange={e => handleApprovalChange("supervisorDate", e.target.value)} /></FormGroup></Col>
                    </Row>
                    <Row>
                        <Col md={6}><FormGroup><Label htmlFor="choices-multiple-remove-button" className="form-label text-dark">Manager</Label><Input value={formData.approvals.manager} onChange={e => handleApprovalChange("manager", e.target.value)} /></FormGroup></Col>
                        <Col md={6}><FormGroup><Label htmlFor="choices-multiple-remove-button" className="form-label text-dark">Date</Label><Input type="date" value={formData.approvals.managerDate} onChange={e => handleApprovalChange("managerDate", e.target.value)} /></FormGroup></Col>
                    </Row>
                    <Row>
                        <Col md={6}><FormGroup><Label htmlFor="choices-multiple-remove-button" className="form-label text-dark">HR</Label><Input value={formData.approvals.hr} onChange={e => handleApprovalChange("hr", e.target.value)} /></FormGroup></Col>
                        <Col md={6}><FormGroup><Label htmlFor="choices-multiple-remove-button" className="form-label text-dark">Date</Label><Input type="date" value={formData.approvals.hrDate} onChange={e => handleApprovalChange("hrDate", e.target.value)} /></FormGroup></Col>
                    </Row>
                </FormSectionCard>

                <div className="text-end mt-3 mb-4">
                    <Button color="primary" type="submit">Submit</Button>
                </div>
            </Form>
        </Container>
    );
};

export default NinetyDayReviewForm;
